import './GenerateView.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListGroup, Modal } from 'react-bootstrap';
import { MdDone, MdCheckBoxOutlineBlank } from 'react-icons/md';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { BasicFilterBar } from '@Components/FilterBar';
import { CometProgressDialog } from '@Components/CometProgressDialog';
import { FilterColumn } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { FLASHTALKING_DSP_TO_VENDOR_MAP } from './TagCreationForm';
import { TagStagingArea } from './TagStagingArea';
import { useCreativeMap } from '../../../redux/creative';
import { useSetError, useSetAreYouSure } from '../../../redux/modals';
import { useStateFunction } from '../../../utils/hooks/useData';
import { TagCreationModal } from './TagCreationModal';
import { TagRow } from './TagRow';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { conditionallyPluralizeWithCount } from '../../../utils/text-utils';
import { useAudioCompanionOptions } from '../../../utils/hooks/useAudioCompanionOptions';
import {
  awaitJSON,
  COMET_WORKER_TIMEOUT_SECONDS,
  CometProgress,
  CreativeLambdaFetch,
  fetchCometSelfInvoke,
  FlashtalkingLambdaFetch,
  StreamingOrdersLambdaFetch
} from '../../../utils/fetch-utils';
import {
  FlashtalkingCometPlacementResult,
  FlashtalkingVendor,
  TagData,
  StagedTagData,
  sanitizeFlashtalkingPlacementName
} from './utils';
import { TagCreationContext } from './tag-creation-context';

const GENERATE_FILTER_BAR_OPTIONS: FilterColumn[] = [
  { name: 'derivedID', label: 'Derived Network' },
  { name: 'network', label: 'Network' },
  { name: 'description', label: 'Description' },
  { name: 'platform', label: 'Platform' },
  { name: 'dsp', label: 'DSP' },
  { name: 'placementName', label: 'Placement Name' }
];

const SendingState = {
  SENDING: 0,
  REFRESHING_DATA: 1,
  CLEARING_STAGED_TAGS: 2,
  COMPLETE: 3
} as const;
type SendingStateType = (typeof SendingState)[keyof typeof SendingState];

const VALID_PLACEMENT_TYPES = ['Audio', 'Multi-tracker', 'Pixel', 'VAST', 'HTML', 'Display'];

const getPlacementType = (stagedTag: StagedTagData): string => {
  if (stagedTag.placementType && VALID_PLACEMENT_TYPES.includes(stagedTag.placementType)) {
    return stagedTag.placementType;
  }
  return stagedTag.platform.includes('Audio') ? 'Audio' : 'VAST';
};

const getAudioCompanionAdid = (adid: unknown): number | null => {
  if (!adid) return null;
  if (typeof adid === 'number') return adid;
  return parseInt(String(adid), 10);
};

const getPlacementTypeLabel = (type: string | undefined): string | undefined => {
  if (type === 'Multi-tracker') return 'Pixel';
  if (type === 'HTML') return 'Display';
  return type;
};

interface GenerateViewProps {
  tagInfo: TagData[];
  suppressedMap: Record<string, boolean>;
  setSuppressedMap: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  shouldRefreshData: boolean;
  setShouldRefreshData: (val: boolean) => void;
}

export const GenerateView = ({
  tagInfo,
  suppressedMap,
  setSuppressedMap,
  shouldRefreshData,
  setShouldRefreshData
}: GenerateViewProps) => {
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const [filter, setFilter] = useStateFunction<(line: object) => boolean>(() => true);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [flashtalkingCampaigns, setFlashtalkingCampaigns] = useState<
    { campaign_id: number; campaign: string; is_managed_service: boolean }[] | undefined
  >();
  const [flashtalkingSites, setFlashtalkingSites] = useState<unknown[] | undefined>();
  const [showSendTagsToNetworksModal, setShowSendTagsToNetworksModal] = useState(false);
  const [selectedTagsForNetworkSendMap, setSelectedTagsForNetworkSendMap] = useState<
    Record<string, unknown>
  >({});
  const { creativeMap } = useCreativeMap({
    company,
    mediaTypes: ['audio', 'display', 'streaming']
  });

  const filteredTagInfo = useMemo<TagData[]>(() => {
    if (!tagInfo || !flashtalkingCampaigns) return tagInfo;
    return tagInfo.filter(tag => {
      const anyTag = tag as any;
      if (anyTag.adServer !== 'ft') return true;
      const campaign = flashtalkingCampaigns.find(c => c.campaign_id === anyTag.campaignID);
      if (!campaign || campaign.is_managed_service) return false;
      return true;
    });
  }, [flashtalkingCampaigns, tagInfo]);

  // Build per-derivedID flashtalking vendor lists without mutating the source tag objects
  const flashtalkingVendorsMap = useMemo<Record<string, unknown[]>>(() => {
    if (!flashtalkingSites) return {};
    const map: Record<string, unknown[]> = {};
    for (const line of filteredTagInfo) {
      const ftSiteMap: Record<string, unknown> = {};
      for (const site of flashtalkingSites as any[]) {
        if (site?.streaming_networks_short_codes?.includes(line.network)) {
          ftSiteMap[site.name] = site;
        }
      }
      map[line.derivedID] = Object.values(ftSiteMap);
    }
    return map;
  }, [filteredTagInfo, flashtalkingSites]);

  // Attach the live flashtalking vendor list to a tag. Derived on every render so
  // a tag snapshotted into the create-tag modal before /getFlashtalkingSites
  // resolves still picks up its vendors once the fetch completes.
  const enrichWithVendors = useCallback(
    (tag: TagData): TagData =>
      flashtalkingSites
        ? {
            ...tag,
            flashtalking_vendors: flashtalkingVendorsMap[tag.derivedID] as FlashtalkingVendor[]
          }
        : tag,
    [flashtalkingSites, flashtalkingVendorsMap]
  );

  const cachedTags = useMemo<StagedTagData[]>(() => {
    const cache = JSON.parse(localStorage.getItem(`${company}_staged_tags`) ?? 'null');
    if (!cache) return [];
    const hoursElapsed = (new Date().getTime() - new Date(cache.date).getTime()) / (60 * 60 * 1000);
    // Only use cached tags from the past work day (~10 hours) to avoid accidentally sending stale tags
    if (hoursElapsed > 10) {
      localStorage.removeItem(`${company}_staged_tags`);
      return [];
    }
    return cache.value;
  }, [company]);

  const [stagedTags, setStagedTags] = useState<StagedTagData[]>(cachedTags);
  const [isSendingTagsToAdServer, setIsSendingTagsToAdServer] = useState<SendingStateType>(
    SendingState.COMPLETE
  );
  const [cometProgress, setCometProgress] = useState<CometProgress>();

  const [filteredLines, missingCount] = useMemo(() => {
    const alreadyAdded = stagedTags.reduce<Record<string, StagedTagData[]>>((acc, tag) => {
      acc[tag.derivedID] = [...(acc[tag.derivedID] || []), tag];
      return acc;
    }, {});
    const derivedMap: Record<string, TagData[]> = {};
    let missing = 0;
    for (const line of filteredTagInfo) {
      const lineWithVendors = enrichWithVendors(line);
      const anyLine = line as any;
      if (
        line.hasPlacement ||
        anyLine.hasSubmittedPendingPlacement ||
        line.hasOverride ||
        suppressedMap[line.derivedID]
      ) {
        if (!onlyMissing && filter(line)) {
          derivedMap[line.derivedID] = [...(derivedMap[line.derivedID] || []), lineWithVendors];
        }
      } else if (!(onlyMissing && alreadyAdded[line.derivedID])) {
        missing++;
        if (filter(line)) {
          derivedMap[line.derivedID] = [...(derivedMap[line.derivedID] || []), lineWithVendors];
        }
      }
    }
    return [Object.values(derivedMap), missing];
  }, [stagedTags, filteredTagInfo, suppressedMap, onlyMissing, enrichWithVendors, filter]);

  useEffect(() => {
    (async () => {
      if (flashtalkingCampaigns == null) {
        const campaignResp = await FlashtalkingLambdaFetch('/getFlashtalkingCampaigns', {
          params: { company }
        });
        setFlashtalkingCampaigns(await awaitJSON(campaignResp));
      }
    })();
  }, [company, flashtalkingCampaigns]);

  useEffect(() => {
    (async () => {
      if (flashtalkingSites == null) {
        const sitesResp = await FlashtalkingLambdaFetch('/getFlashtalkingSites', { params: {} });
        setFlashtalkingSites(await awaitJSON(sitesResp));
      }
    })();
  }, [company, flashtalkingSites]);

  const suppressTag = useCallback(
    async (derivedID: string) => {
      try {
        await setAreYouSure({
          title: 'Suppressing Tag',
          intent: Intent.ERROR,
          message: `You are about to mark "${derivedID}" as tag-less. You should only do this if it corresponds to a flight you know will never run. Are you sure absolutely certain this should not have a tag?`,
          okayText: "I'm Certain"
        });
      } catch {
        return;
      }
      setSuppressedMap(map => ({ ...map, [derivedID]: true }));
      try {
        await StreamingOrdersLambdaFetch('/tag_override', {
          method: 'POST',
          body: { derivedID }
        });
      } catch (e) {
        setError({ message: e.message, reportError: e });
        setSuppressedMap(prev => {
          const { [derivedID]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [setError, setAreYouSure, setSuppressedMap]
  );

  const [tagCreationModalData, setTagCreationModalData] = useState<TagData>();
  const [isTagCreationModalEditMode, setIsTagCreationModalEditMode] = useState(false);
  const [showTagCreationModal, setShowTagCreationModal] = useState(false);
  const [additionalSourceTags, setAdditionalSourceTags] = useState<TagData[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set<string>());

  const openTagCreationModal = useCallback(
    (tag: TagData, isEditMode: boolean, extraTags: TagData[] = []) => {
      setTagCreationModalData(tag);
      setIsTagCreationModalEditMode(isEditMode);
      setAdditionalSourceTags(extraTags);
      setShowTagCreationModal(true);
    },
    []
  );

  const closeTagCreationModal = useCallback(() => {
    setShowTagCreationModal(false);
    setIsTagCreationModalEditMode(false);
  }, []);

  const stageTags = useCallback((tags: StagedTagData[]) => {
    setStagedTags(current => [...current, ...tags]);
    setShowTagCreationModal(false);
    setSelectedRowIds(new Set());
  }, []);

  const toggleRowSelection = useCallback((derivedID: string) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(derivedID)) {
        next.delete(derivedID);
      } else {
        next.add(derivedID);
      }
      return next;
    });
  }, []);

  const { allSelected, someSelected, visibleSelectedCount } = useMemo(() => {
    const allVisibleIds = filteredLines.map(tags => tags[0].derivedID);
    const selectedCount = allVisibleIds.filter(id => selectedRowIds.has(id)).length;
    const all = allVisibleIds.length > 0 && selectedCount === allVisibleIds.length;
    const some = selectedCount > 0 && !all;
    return { allSelected: all, someSelected: some, visibleSelectedCount: selectedCount };
  }, [filteredLines, selectedRowIds]);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(filteredLines.map(tags => tags[0].derivedID)));
    }
  }, [allSelected, filteredLines]);

  const openBulkTagCreationModal = useCallback(() => {
    const sourceTags = filteredLines
      .filter(tags => selectedRowIds.has(tags[0].derivedID))
      .map(tags => tags[0]);
    if (sourceTags.length === 0) return;
    openTagCreationModal(sourceTags[0], false, sourceTags.slice(1));
  }, [filteredLines, openTagCreationModal, selectedRowIds]);

  const sendTagsToFlashTalking = useCallback(async () => {
    const siteIDMap: Record<string, number> = {};
    if (flashtalkingSites) {
      for (const site of flashtalkingSites as any[]) {
        siteIDMap[site.name] = site.id;
      }
    }
    setIsSendingTagsToAdServer(SendingState.SENDING);
    try {
      const commitCreativesPlacements: unknown[] = [];
      const placementMap: Record<number, unknown> = {};
      const transcodeSiteMap: Record<number, number> = {};
      let placementIndex = 0;
      for (const stagedTag of stagedTags) {
        if (stagedTag.adServer === 'Flashtalking') {
          placementMap[placementIndex] = {
            campaignID: stagedTag.campaignID,
            id: placementIndex,
            name: sanitizeFlashtalkingPlacementName(stagedTag.name),
            dimensions: stagedTag.dimensions,
            // FT rejects full ISO timestamps on placement create ("Malformed payload body"),
            // and staged tags can carry one from an older /tag_info response.
            startDate: (stagedTag.startDate ?? TODAY).slice(0, 10),
            endDate: stagedTag.endDate?.slice(0, 10),
            clickTag1: stagedTag.clickthroughUrl,
            placementType: getPlacementType(stagedTag),
            costTypeMedia: 'CPM',
            costRateMedia: stagedTag.platform?.includes('Audio') ? 0.053 : 0.135,
            site: { name: stagedTag.vendor },
            placementCreatives: Object.keys(stagedTag.creativeAllocationMap || {}).map(isci => ({
              id: ['Multi-tracker', 'Pixel'].includes(stagedTag.placementType ?? '')
                ? creativeMap?.[isci]?.flashtalking_pixel_id
                : creativeMap?.[isci]?.flashtalking_id,
              name: isci,
              rotationWeight: (stagedTag.creativeAllocationMap as any)?.[isci],
              startDate: TODAY,
              clickTag1: stagedTag.clickthroughUrlMap?.[isci] ?? null,
              audioCompanion: getAudioCompanionAdid(stagedTag.audioCompanionMap?.[isci]?.adid)
            }))
          };
          for (let i = 1; i <= 10; i++) {
            if (stagedTag.extraTrackingPixels?.[i - 1]) {
              (placementMap[placementIndex] as any)[`thirdPartyImpression${i}`] =
                stagedTag.extraTrackingPixels[i - 1];
            }
          }
          if (stagedTag.transcodeVendor && siteIDMap[stagedTag.transcodeVendor]) {
            transcodeSiteMap[placementIndex] = siteIDMap[stagedTag.transcodeVendor];
          }
          placementIndex--;
        }
      }
      if (Object.keys(placementMap).length > 0) {
        setCometProgress(undefined);
        const returnedPlacementMap: Record<string, FlashtalkingCometPlacementResult> =
          await fetchCometSelfInvoke(
            FlashtalkingLambdaFetch,
            '/sendPlacementsToFlashtalking',
            {
              company,
              placementMap,
              transcodeSiteMap
            },
            { timeout: COMET_WORKER_TIMEOUT_SECONDS, onProgress: setCometProgress }
          );
        const filteredReturnedPlacementMap: Record<string, FlashtalkingCometPlacementResult> = {};
        let returnedCreativesResponsePlacementMap: Record<
          string,
          FlashtalkingCometPlacementResult
        > = {};
        const unmatchedPlacements: string[] = [];
        if (returnedPlacementMap && Object.keys(returnedPlacementMap).length > 0) {
          for (const id of Object.keys(returnedPlacementMap)) {
            if (returnedPlacementMap[id].error) continue;
            filteredReturnedPlacementMap[id] = returnedPlacementMap[id];
            const creatives: unknown[] = [];
            let matched = false;
            for (const stagedTag of stagedTags) {
              if (
                stagedTag.adServer === 'Flashtalking' &&
                returnedPlacementMap[id].name === sanitizeFlashtalkingPlacementName(stagedTag.name)
              ) {
                matched = true;
                for (const isci of Object.keys(stagedTag.creativeAllocationMap || {})) {
                  creatives.push({ isci, pct: (stagedTag.creativeAllocationMap as any)[isci] });
                }
                commitCreativesPlacements.push({
                  placementID: id,
                  creatives,
                  clickthroughUrlMap: stagedTag.clickthroughUrlMap || {},
                  audioCompanionMap: stagedTag.audioCompanionMap || {},
                  extraTrackingPixels: stagedTag.extraTrackingPixels || [],
                  dimensions: stagedTag.dimensions
                });
              }
            }
            if (!matched) {
              unmatchedPlacements.push(returnedPlacementMap[id].name);
              delete filteredReturnedPlacementMap[id];
            }
          }
          if (commitCreativesPlacements.length) {
            await CreativeLambdaFetch('/commitCreatives', {
              method: 'POST',
              body: { date: TODAY, company, placements: commitCreativesPlacements }
            });
            setCometProgress(undefined);
            returnedCreativesResponsePlacementMap = await fetchCometSelfInvoke(
              FlashtalkingLambdaFetch,
              '/sendCreativeRotationsToFlashtalking',
              { company, placementMap: filteredReturnedPlacementMap },
              { timeout: COMET_WORKER_TIMEOUT_SECONDS, onProgress: setCometProgress }
            );
          }
        }
        const failed: string[] = [];
        const succeeded: string[] = [];
        const errorMap: Record<string, boolean> = {};
        for (const name of unmatchedPlacements) {
          failed.push(
            `${name}: placement was created but no creatives were assigned (name mismatch)`
          );
        }
        for (const key of Object.keys(returnedPlacementMap ?? {})) {
          if (returnedPlacementMap[key].error) {
            failed.push(`${returnedPlacementMap[key].name}: ${returnedPlacementMap[key].error}`);
            errorMap[returnedPlacementMap[key].name] = true;
          }
        }
        for (const key of Object.keys(returnedCreativesResponsePlacementMap ?? {})) {
          if (returnedCreativesResponsePlacementMap[key].error) {
            failed.push(
              `${returnedCreativesResponsePlacementMap[key].name} (creative rotation): ${returnedCreativesResponsePlacementMap[key].error}`
            );
            errorMap[returnedCreativesResponsePlacementMap[key].name] = true;
          } else {
            succeeded.push(returnedCreativesResponsePlacementMap[key].name);
          }
        }
        const successText = succeeded.length
          ? `✓ Created (${succeeded.length}):\n${succeeded.join('\n')}`
          : '';
        const errorText = failed.length ? `✗ Failed (${failed.length}):\n${failed.join('\n')}` : '';
        if (errorText) {
          const messageLines = [successText, errorText].filter(Boolean);
          setError({
            title: succeeded.length ? 'Partial Success' : 'Error',
            message: messageLines.join('\n\n')
          });
          setStagedTags(prev =>
            prev.filter(tag => errorMap[sanitizeFlashtalkingPlacementName(tag.name)])
          );
          setIsSendingTagsToAdServer(SendingState.COMPLETE);
        } else {
          setIsSendingTagsToAdServer(SendingState.REFRESHING_DATA);
          setError({
            title: 'Success',
            intent: Intent.SUCCESS,
            message: `Placements uploaded!\n${successText}`
          });
        }
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
      setIsSendingTagsToAdServer(SendingState.COMPLETE);
    }
  }, [company, creativeMap, flashtalkingSites, setError, stagedTags]);

  const sendTagsToAdServers = useCallback(async () => {
    await sendTagsToFlashTalking();
  }, [sendTagsToFlashTalking]);

  const sendTagsToNetworks = useCallback(
    async (isTest = false) => {
      setIsSendingTagsToAdServer(SendingState.SENDING);
      try {
        const placementMap: Record<string, unknown> = {};
        for (const key of Object.keys(selectedTagsForNetworkSendMap)) {
          const tag = selectedTagsForNetworkSendMap[key] as any;
          if (tag != null) {
            placementMap[key] = {
              campaign_id: tag.campaignID,
              campaign_name: tag.campaignName,
              company,
              derived_id: tag.derivedID,
              flashtalking_placement_type: tag.flashtalkingPlacementType,
              placement_id: tag.placement_id,
              placement_name: tag.placement_name,
              platform: tag.platform,
              start_date: (tag.startDate ?? TODAY).slice(0, 10),
              vendor: FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp] ?? tag.placementVendor
            };
          }
        }
        await FlashtalkingLambdaFetch('/sendFlashtalkingPlacementTagsToNetworks', {
          method: 'POST',
          body: { placementMap, isTest }
        });
        setError({ title: 'Success', intent: Intent.SUCCESS, message: 'Placement Tags Sent!' });
      } catch (e) {
        setError({ message: e.message, reportError: e });
      } finally {
        setIsSendingTagsToAdServer(SendingState.COMPLETE);
        setShowSendTagsToNetworksModal(false);
      }
    },
    [company, selectedTagsForNetworkSendMap, setError]
  );

  useEffect(() => {
    // Force the order of refreshing page data then clearing staged tags so that
    // there's no glitchiness where staged tags reappear in the missing tags list after being sent.
    if (isSendingTagsToAdServer === SendingState.REFRESHING_DATA) {
      setShouldRefreshData(true);
      setIsSendingTagsToAdServer(SendingState.CLEARING_STAGED_TAGS);
    }
    if (isSendingTagsToAdServer === SendingState.CLEARING_STAGED_TAGS && !shouldRefreshData) {
      setStagedTags([]);
      setIsSendingTagsToAdServer(SendingState.COMPLETE);
    }
  }, [isSendingTagsToAdServer, setShouldRefreshData, shouldRefreshData]);

  useEffect(() => {
    localStorage.setItem(
      `${company}_staged_tags`,
      JSON.stringify({ value: stagedTags, date: new Date() })
    );
  }, [company, stagedTags]);

  const audioCompanionOptions = useAudioCompanionOptions();
  const contextValue = useMemo(() => ({ audioCompanionOptions }), [audioCompanionOptions]);

  const ftTagsForNetworkSend = (filteredTagInfo ?? []).filter(
    (tag: any) => (tag.adServer === 'ft' || tag.adServer === 'Flashtalking') && tag.hasPlacement
  );

  return (
    <TagCreationContext.Provider value={contextValue}>
      <Modal
        centered
        onHide={() => setShowSendTagsToNetworksModal(false)}
        show={showSendTagsToNetworksModal}
        size='lg'
        dialogClassName='send-ft-tags-modal'
      >
        <Modal.Header>
          <Modal.Title>Send Flashtalking Tags to Networks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ListGroup>
            {ftTagsForNetworkSend.map((tag: any) => {
              const isChecked = selectedTagsForNetworkSendMap[tag.placement_id] != null;
              const toggleTag = () =>
                setSelectedTagsForNetworkSendMap(map => ({
                  ...map,
                  [tag.placement_id]: map[tag.placement_id] != null ? undefined : tag
                }));
              return (
                <ListGroup.Item key={tag.placement_id}>
                  <div className='ft-placement-row'>
                    <Checkbox checked={isChecked} onChange={toggleTag} />
                    <InteractiveDiv onClick={toggleTag}>
                      <div>Start Date: {(tag.startDate ?? '').slice(0, 10)}</div>
                      <div>Placement ID: {tag.placement_id}</div>
                      <div>Placement Name: {tag.placement_name}</div>
                      <div>Vendor: {tag.placementVendor}</div>
                      <div>Campaign ID: {tag.campaignID}</div>
                      <div>Campaign Name: {tag.campaignName}</div>
                      <div>
                        Placement Type: {getPlacementTypeLabel(tag.flashtalkingPlacementType)}
                      </div>
                      {tag.dsp && <div>DSP: {tag.dsp}</div>}
                      {tag.dimensions && <div>Dimensions: {tag.dimensions}</div>}
                    </InteractiveDiv>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            onClick={() => setShowSendTagsToNetworksModal(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setShowSendTagsToNetworksModal(false);
              setAreYouSure({
                title: "You're sending Flashtalking Tags to your email",
                message:
                  'You are about to email these placement tags directly to your email. Are you sure you want to continue?',
                intent: Intent.WARNING,
                cancelText: 'Never mind',
                okayText: 'Yes',
                onCancel: () => setShowSendTagsToNetworksModal(true),
                onOkay: () => sendTagsToNetworks(true)
              });
            }}
          >
            Test Send Tags
          </Button>
          <Button
            onClick={() => {
              setShowSendTagsToNetworksModal(false);
              setAreYouSure({
                title: "You're sending Flashtalking Tags to Networks",
                message:
                  'You are about to email these placement tags directly to their respective networks. Are you sure you want to continue?',
                intent: Intent.WARNING,
                cancelText: 'Never mind',
                okayText: 'Yes',
                onCancel: () => setShowSendTagsToNetworksModal(true),
                onOkay: () => sendTagsToNetworks()
              });
            }}
          >
            Send
          </Button>
        </Modal.Footer>
      </Modal>
      <CometProgressDialog
        show={isSendingTagsToAdServer === SendingState.SENDING}
        title='Uploading to Flashtalking'
        progress={cometProgress}
      />
      <div className='tag-view' data-testid='generate-view'>
        <div className='control-bar'>
          <Button
            size={Size.LG}
            appearance={onlyMissing ? Appearance.FILLED : Appearance.OUTLINED}
            leadingElement={onlyMissing ? <MdDone /> : <MdCheckBoxOutlineBlank />}
            onClick={() => setOnlyMissing(v => !v)}
          >
            Missing Tags Only ({missingCount})
          </Button>
          <BasicFilterBar options={GENERATE_FILTER_BAR_OPTIONS} onFilter={f => setFilter(f)} />
        </div>
        <div className='content'>
          <div className='selection-toolbar'>
            <Checkbox
              id='select-all-rows'
              label='Select All'
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
            />
            {visibleSelectedCount > 0 && (
              <Button size={Size.LG} onClick={openBulkTagCreationModal}>
                Create Tags for{' '}
                {conditionallyPluralizeWithCount(visibleSelectedCount, 'Selected Row')}
              </Button>
            )}
          </div>
          <ListGroup>
            {filteredLines.map(tags => {
              const { derivedID } = tags[0];
              return (
                <TagRow
                  key={derivedID}
                  tags={tags}
                  suppressTag={suppressTag}
                  onAdd={openTagCreationModal}
                  isSelected={selectedRowIds.has(derivedID)}
                  onToggleSelect={toggleRowSelection}
                />
              );
            })}
          </ListGroup>
          <TagStagingArea
            stagedTags={stagedTags}
            setStagedTags={setStagedTags}
            isSending={isSendingTagsToAdServer !== SendingState.COMPLETE}
            openTagCreationModal={openTagCreationModal}
          />
          <div className='generate-bar' data-testid='generate-bar'>
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              onClick={() => setShowSendTagsToNetworksModal(true)}
            >
              Send Flashtalking Tags to Networks
            </Button>
            <Button
              size={Size.LG}
              disabled={isSendingTagsToAdServer !== SendingState.COMPLETE}
              onClick={() =>
                setAreYouSure({
                  title: "You're sending an update to Flashtalking",
                  message:
                    'You are about to upload these new placements directly to Flashtalking. Are you sure you want to continue?',
                  intent: Intent.WARNING,
                  cancelText: 'Never mind',
                  okayText: 'Yes',
                  onOkay: sendTagsToAdServers
                })
              }
            >
              Send Tags to Flashtalking
            </Button>
          </div>
          {tagCreationModalData && (
            <TagCreationModal
              initialTag={enrichWithVendors(tagCreationModalData)}
              setInitialTag={setTagCreationModalData}
              show={showTagCreationModal}
              startingPage='initialForm'
              onClose={closeTagCreationModal}
              stageTags={stageTags}
              isEditMode={isTagCreationModalEditMode}
              flashtalkingCampaigns={flashtalkingCampaigns}
              additionalSourceTags={additionalSourceTags.map(enrichWithVendors)}
            />
          )}
        </div>
      </div>
    </TagCreationContext.Provider>
  );
};
